import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import { isBlobUrl } from 'shared/utils/blobUrl';
import {
  isDisplayableNonPdfSource,
  isPdfSignedSource,
  isPersistedSource,
} from 'shared/utils/sourceUrl';
import { fileKindService } from '../fileKindService';
import {
  DrawingSnapshot,
  FileSnapshot,
  ListImageDisplayState,
  ListImageDisplayStateInput,
} from './types';

export class ThumbnailService {
  resolveStoredBlobUrl(
    drawing: DrawingSnapshot | undefined,
    file: FileSnapshot | undefined,
    getItem: (key: string) => string | null | undefined
  ): string | undefined {
    if (!file) {
      return undefined;
    }

    if (drawing?.mergedImageUrl) {
      const merged = getItem(drawing.mergedImageUrl);
      if (merged) {
        return merged;
      }
    }

    return undefined;
  }

  resolveListImageDisplayState(
    input: ListImageDisplayStateInput
  ): ListImageDisplayState {
    const showSpinner =
      input.src === null || (!input.isLoaded && !input.error);

    return { showSpinner, imageOpacity: showSpinner ? 0 : 1 };
  }

  resolveListImageSrc(
    error: boolean,
    drawing: DrawingSnapshot | undefined,
    file: FileSnapshot
  ): string | null {
    if (error) {
      return DOCUMENT_ERROR_SOURCE;
    }

    if (drawing?.clear) {
      return this.resolveListImageBaseFallback(file);
    }

    const mergedSource = this.resolveMergedSourceForMiniature(
      drawing,
      file
    );
    if (mergedSource) {
      return mergedSource;
    }

    return this.resolveListImageBaseFallback(file);
  }

  resolveMiniatureSrc(
    error: boolean,
    storedBlobUrl: string | null | undefined,
    drawing: DrawingSnapshot | undefined,
    file: FileSnapshot
  ): string | null {
    if (error) {
      return DOCUMENT_ERROR_SOURCE;
    }

    const isConvertedPdf = fileKindService.isConvertedPdf(file);

    if (drawing?.clear) {
      return this.resolveMiniatureBaseFallback(file, isConvertedPdf);
    }

    if (storedBlobUrl) {
      return storedBlobUrl;
    }

    const mergedSource = this.resolveMergedSourceForMiniature(
      drawing,
      file
    );
    if (mergedSource) {
      return mergedSource;
    }

    if (fileKindService.hasConvertedPdfPreviewSource(file)) {
      const signedConvertedPreview = this.resolveMiniatureBaseFallback(
        file,
        isConvertedPdf
      );
      if (signedConvertedPreview) {
        return signedConvertedPreview;
      }
    }

    return this.resolveMiniatureBaseFallback(file, isConvertedPdf);
  }

  private resolveMergedSourceForMiniature(
    drawing: DrawingSnapshot | undefined,
    file: FileSnapshot
  ): string | undefined {
    return this.resolveMergedSource(drawing, file, true);
  }

  private resolveMergedSource(
    drawing: DrawingSnapshot | undefined,
    file: FileSnapshot,
    includeMergedThumbnailSignedRequest: boolean
  ): string | undefined {
    if (isDisplayableNonPdfSource(drawing?.thumbnailMergedImageUrl)) {
      return drawing.thumbnailMergedImageUrl;
    }

    if (isPersistedSource(file.data?.thumbnailMergedSrc)) {
      return file.data.thumbnailMergedSrc;
    }

    if (
      includeMergedThumbnailSignedRequest &&
      isPersistedSource(file.signedRequest?.mergedThumbnailSignedRequest)
    ) {
      return file.signedRequest.mergedThumbnailSignedRequest;
    }

    if (isPersistedSource(file.signedRequest?.mergedSignedRequest)) {
      return file.signedRequest.mergedSignedRequest;
    }

    const mergedImageUrl = drawing?.mergedImageUrl;
    if (
      isDisplayableNonPdfSource(mergedImageUrl) &&
      !isBlobUrl(mergedImageUrl)
    ) {
      return mergedImageUrl;
    }

    if (isPersistedSource(file.data?.mergedSrc)) {
      return file.data.mergedSrc;
    }

    if (isPersistedSource(file.signedRequest?.drawingSignedRequest)) {
      return file.signedRequest.drawingSignedRequest;
    }

    return undefined;
  }

  private resolveListImageBaseFallback(file: FileSnapshot): string | null {
    const pdfFallback = this.resolvePdfListSource(file);
    if (fileKindService.isPdf(file)) {
      if (pdfFallback !== undefined) {
        return pdfFallback;
      }

      return this.resolveLoadingOrError(file);
    }

    if (pdfFallback !== undefined) {
      return pdfFallback;
    }

    // FIX: Use thumbnail-specific preview source to avoid loading full images
    const displayablePreviewSource =
      this.resolveDisplayablePreviewSourceForThumbnail(file);
    if (displayablePreviewSource) {
      return displayablePreviewSource;
    }

    return this.resolveLoadingOrError(file);
  }

  private resolvePdfListSource(
    file: FileSnapshot
  ): string | null | undefined {
    if (!fileKindService.isPdf(file)) {
      return undefined;
    }

    if (isPersistedSource(file.signedRequest?.thumbnailSignedRequest)) {
      return file.signedRequest.thumbnailSignedRequest;
    }

    // Keep showing converted previews while another file upload/drawing triggers
    // a list resync that temporarily marks this entry as loading.
    if (
      fileKindService.isConvertedPdf(file) &&
      isPersistedSource(file.signedRequest?.imageSignedRequest)
    ) {
      return file.signedRequest.imageSignedRequest;
    }

    if (this.isLoading(file)) {
      return null;
    }

    return undefined;
  }

  private resolveMiniatureBaseFallback(
    file: FileSnapshot,
    isConvertedPdf: boolean
  ): string | null {
    if (isPersistedSource(file.signedRequest?.thumbnailSignedRequest)) {
      return file.signedRequest.thumbnailSignedRequest;
    }


    const convertedPdfFallback =
      this.resolveConvertedPdfThumbnailFallback(file, isConvertedPdf);
    if (convertedPdfFallback !== undefined) {
      return convertedPdfFallback;
    }

    const displayablePreviewSource =
      this.resolveDisplayablePreviewSourceForThumbnail(file);
    if (displayablePreviewSource) {
      return displayablePreviewSource;
    }

    return this.resolveLoadingOrError(file);
  }

  private resolveDisplayablePreviewSourceForThumbnail(
    file: FileSnapshot
  ): string | undefined {
    if (isDisplayableNonPdfSource(file.thumbnail)) {
      return file.thumbnail;
    }

    if (
      fileKindService.isPdf(file) &&
      isPersistedSource(file.src) &&
      !isPdfSignedSource(file.src)
    ) {
      return file.src;
    }

    return undefined;
  }

  private resolveDisplayablePreviewSource(
    file: FileSnapshot
  ): string | undefined {
    if (isDisplayableNonPdfSource(file.thumbnail)) {
      return file.thumbnail;
    }

    if (isDisplayableNonPdfSource(file.src)) {
      return file.src;
    }

    if (isDisplayableNonPdfSource(file.downloadSrc)) {
      return file.downloadSrc;
    }

    return undefined;
  }

  private resolveConvertedPdfThumbnailFallback(
    file: FileSnapshot,
    isConvertedPdf: boolean
  ): string | null | undefined {
    if (!isConvertedPdf) {
      return undefined;
    }

    if (isPersistedSource(file.signedRequest?.thumbnailSignedRequest)) {
      return file.signedRequest.thumbnailSignedRequest;
    }

    if (isPersistedSource(file.signedRequest?.imageSignedRequest)) {
      return file.signedRequest.imageSignedRequest;
    }

    const displayablePreviewSource =
      this.resolveDisplayablePreviewSource(file);
    if (
      displayablePreviewSource &&
      isPersistedSource(displayablePreviewSource)
    ) {
      return displayablePreviewSource;
    }

    if (this.isLoading(file)) {
      return null;
    }

    return undefined;
  }

  private resolveLoadingOrError(file: FileSnapshot): string | null {
    if (this.isLoading(file)) {
      return null;
    }

    return DOCUMENT_ERROR_SOURCE;
  }

  private isLoading(file: FileSnapshot): boolean {
    return file.loading === true;
  }

}

export const thumbnailService = new ThumbnailService();

export type {
  DrawingSnapshot,
  FileSnapshot,
  ListImageDisplayState,
  ListImageDisplayStateInput,
  SignedRequestSnapshot,
} from './types';
